import { useCallback, useRef, useState } from 'react'
import { SxProps, Typography, styled } from '@mui/material'
import i18next from 'i18next'
import { useHistoryCustomBack } from 'weblite-router'
import { TimePicker } from '@web-lite/scroll-picker'
import { t } from '$i18n'
import { Label } from '$common/label'
import { SelectButton } from '$common/buttons'
import { BottomDrawer } from '$common/bottom-drawer'
import { SecondaryButton } from '$common/buttons/secondary-button'
import { formatToLocaleDateTime } from '$helper/functions/date'

interface Props {
  onSelect: (date: Date) => void
  label?: string
  value: Date
  required?: boolean
  placeholder?: string
  startDate: Date
  endDate: Date
  rangeIsDynamic?: boolean
  drawerTitle?: string
  labelProps?: Omit<Parameters<typeof Label>[0], 'title' | 'required'>
  formatValue?: (value: Date) => string
  selectButtonSx?: SxProps
}

export function SelectDateTime({
  onSelect,
  label,
  value,
  required,
  placeholder,
  startDate,
  endDate,
  drawerTitle,
  labelProps,
  formatValue,
  selectButtonSx,
  rangeIsDynamic = false,
}: Props) {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState(value)

  const startRef = useRef(startDate)
  const endRef = useRef(endDate)

  if (rangeIsDynamic) {
    startRef.current = startDate
    endRef.current = endDate
  }

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  useHistoryCustomBack(handleClose, [handleClose], open)

  const onDone = useCallback(() => {
    onSelect(date)
    handleClose()
  }, [date])

  return (
    <div>
      {label && <Label required={required} title={label} {...labelProps} />}
      <SelectButton
        sx={selectButtonSx}
        value={formatValue ? formatValue(value) : formatToLocaleDateTime(value)}
        onClick={() => setOpen(true)}
        placeholder={placeholder}
      />
      <BottomDrawer isOpen={open} onClose={handleClose}>
        <DrawerRoot>
          {drawerTitle && (
            <Typography mb="64px" variant="subtitle1" color="secondary700">
              {drawerTitle}
            </Typography>
          )}
          <TimePicker
            locale={i18next.language}
            onChange={setDate}
            theme={null}
            end={endRef.current}
            selected={value}
            start={startRef.current}
          />
          <SecondaryButton
            sx={{ mt: '16px' }}
            onClick={onDone}
            text={t('common.confirm')}
          />
        </DrawerRoot>
      </BottomDrawer>
    </div>
  )
}

const DrawerRoot = styled('div')`
  padding: 16px;
  display: flex;
  flex-direction: column;
  .MuiDivider-root {
    border-color: ${({ theme }) => theme.palette.neutral400};
  }
`
